import * as state from '@/components/Analysis/state';
import { Box, Button, Grow, Stack, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';

import { ES_MAPPING_KEYS } from '@/components/EsSheet/data';
import { getNextValidStep } from '../shared/validation';
import {
  COF_COLUMNS,
  COF_VALID_VALUES,
  DC_COLUMNS,
  DC_VALID_VALUES,
  EDD_COLUMNS,
  EDD_VALID_VALUES,
  GOVERNING_COLUMNS,
  GOVERNING_VALID_VALUES,
  MATERIAL_TYPE_COLUMNS,
  MATERIAL_TYPE_VALID_VALUES,
  RC_COLUMNS,
  RC_VALID_VALUES,
} from '@/constants';
import { abyssColors } from '@/theme/colors';
import { useWizard } from '../context';
import { Banner } from '@/components/shared/Banner';
import { PanelHeader } from '../styles';
import { MappingSection } from './MappingSection';

export type ValueMapping = {
  userValue: string;
  systemValue: string;
};

export const MapValues = () => {
  const [originalEsSheet] = useAtom(state.originalEsSheet);
  const [editableEsSheet, setEditableEsSheet] = useAtom(state.editableEsSheet);
  const [cofMappings, setCofMappings] = useState<Record<string, ValueMapping[]>>({});
  const [dcMappings, setDcMappings] = useState<ValueMapping[]>([]);
  const [eddMappings, setEddMappings] = useState<ValueMapping[]>([]);
  const [governingMappings, setGoverningMappings] = useState<Record<string, ValueMapping[]>>({});
  const [materialTypeMappings, setMaterialTypeMappings] = useState<ValueMapping[]>([]);
  const [rcMappings, setRcMappings] = useState<Record<string, ValueMapping[]>>({});
  const { goToPreviousStep, goToStep } = useWizard();

  const getColumnIndex = (systemColumnName: string): number => {
    if (!editableEsSheet) return -1;

    const mappings = editableEsSheet.mappings || {};

    // Find which source column is mapped to this system field name
    // mappings format: { "User Column Name": "risk_safety_cof" }
    // We need to find the key for the system field name
    const sourceColumn = Object.keys(mappings).find((sourceCol) => {
      const targetKey = mappings[sourceCol];
      // Find the ES_MAPPING_KEY that matches this targetKey and has the systemColumnName
      const matchingField = ES_MAPPING_KEYS.find(
        (field) => field.key === targetKey && field.name === systemColumnName
      );
      return !!matchingField;
    });

    if (!sourceColumn) return -1;

    return editableEsSheet.headers.indexOf(sourceColumn);
  };

  const getUniqueValues = (columnNames: string[]): string[] => {
    if (!editableEsSheet) return [];

    const uniqueValues = new Set<string>();
    columnNames.forEach((columnName) => {
      const columnIndex = getColumnIndex(columnName);
      if (columnIndex !== -1) {
        editableEsSheet.data.forEach((row) => {
          const value = row[columnIndex];
          if (value && value.trim() !== '') {
            uniqueValues.add(value.trim());
          }
        });
      }
    });

    return Array.from(uniqueValues).sort();
  };

  const autoMapValue = (userValue: string, validValues: string[]): string => {
    // Try exact match first
    const exactMatch = validValues.find((v) => v === userValue);
    if (exactMatch) return exactMatch;

    // Try case-insensitive match
    const caseInsensitiveMatch = validValues.find(
      (v) => v.toLowerCase() === userValue.toLowerCase()
    );
    if (caseInsensitiveMatch) return caseInsensitiveMatch;

    // Try trimmed match
    const trimmedMatch = validValues.find(
      (v) => v.toLowerCase().trim() === userValue.toLowerCase().trim()
    );
    if (trimmedMatch) return trimmedMatch;

    return '';
  };

  useEffect(() => {
    if (!editableEsSheet) return;

    const cofMappingsInit: Record<string, ValueMapping[]> = {};
    COF_COLUMNS.forEach((column) => {
      const uniqueValues = getUniqueValues([column]);
      cofMappingsInit[column] = uniqueValues.map((value) => ({
        userValue: value,
        systemValue:
          editableEsSheet.valueMappings?.[column]?.[value] ?? autoMapValue(value, COF_VALID_VALUES),
      }));
    });
    setCofMappings(cofMappingsInit);

    const dcUniqueValues = getUniqueValues(DC_COLUMNS);
    setDcMappings(
      dcUniqueValues.map((value) => {
        const savedMapping = DC_COLUMNS.reduce<string | undefined>(
          (found, col) => found ?? editableEsSheet.valueMappings?.[col]?.[value],
          undefined
        );
        return {
          userValue: value,
          systemValue: savedMapping ?? autoMapValue(value, DC_VALID_VALUES),
        };
      })
    );

    const eddUniqueValues = getUniqueValues(EDD_COLUMNS);
    setEddMappings(
      eddUniqueValues.map((value) => {
        const savedMapping = EDD_COLUMNS.reduce<string | undefined>(
          (found, col) => found ?? editableEsSheet.valueMappings?.[col]?.[value],
          undefined
        );
        return {
          userValue: value,
          systemValue: savedMapping ?? autoMapValue(value, EDD_VALID_VALUES),
        };
      })
    );

    const governingMappingsInit: Record<string, ValueMapping[]> = {};
    GOVERNING_COLUMNS.forEach((column) => {
      const uniqueValues = getUniqueValues([column]);
      governingMappingsInit[column] = uniqueValues.map((value) => ({
        userValue: value,
        systemValue:
          editableEsSheet.valueMappings?.[column]?.[value] ??
          autoMapValue(value, GOVERNING_VALID_VALUES),
      }));
    });
    setGoverningMappings(governingMappingsInit);

    const rcMappingsInit: Record<string, ValueMapping[]> = {};
    RC_COLUMNS.forEach((column) => {
      const uniqueValues = getUniqueValues([column]);
      rcMappingsInit[column] = uniqueValues.map((value) => ({
        userValue: value,
        systemValue:
          editableEsSheet.valueMappings?.[column]?.[value] ?? autoMapValue(value, RC_VALID_VALUES),
      }));
    });
    setRcMappings(rcMappingsInit);

    const materialTypeUniqueValues = getUniqueValues(MATERIAL_TYPE_COLUMNS);
    setMaterialTypeMappings(
      materialTypeUniqueValues.map((value) => {
        const savedMapping = MATERIAL_TYPE_COLUMNS.reduce<string | undefined>(
          (found, col) => found ?? editableEsSheet.valueMappings?.[col]?.[value],
          undefined
        );
        return {
          userValue: value,
          systemValue: savedMapping ?? autoMapValue(value, MATERIAL_TYPE_VALID_VALUES),
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableEsSheet]);

  const handleCofMappingChange = (column: string, index: number, systemValue: string) => {
    setCofMappings((prev) => {
      const updated = { ...prev };
      updated[column] = [...updated[column]];
      updated[column][index] = {
        ...updated[column][index],
        systemValue,
      };
      return updated;
    });
  };

  const handleDcMappingChange = (index: number, systemValue: string) => {
    setDcMappings((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        systemValue,
      };
      return updated;
    });
  };

  const handleEddMappingChange = (index: number, systemValue: string) => {
    setEddMappings((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        systemValue,
      };
      return updated;
    });
  };

  const handleMaterialTypeMappingChange = (index: number, systemValue: string) => {
    setMaterialTypeMappings((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        systemValue,
      };
      return updated;
    });
  };

  const handleRcMappingChange = (column: string, index: number, systemValue: string) => {
    setRcMappings((prev) => {
      const updated = { ...prev };
      updated[column] = [...updated[column]];
      updated[column][index] = {
        ...updated[column][index],
        systemValue,
      };
      return updated;
    });
  };

  const handleGoverningMappingChange = (column: string, index: number, systemValue: string) => {
    setGoverningMappings((prev) => {
      const updated = { ...prev };
      updated[column] = [...updated[column]];
      updated[column][index] = {
        ...updated[column][index],
        systemValue,
      };
      return updated;
    });
  };

  const areAllValuesMapped = useMemo(() => {
    const allCofMapped = Object.values(cofMappings).every((mappings) =>
      mappings.every((m) => m.systemValue !== '')
    );
    const allDcMapped = dcMappings.every((m) => m.systemValue !== '');
    const allEddMapped = eddMappings.every((m) => m.systemValue !== '');
    const allGoverningMapped = Object.values(governingMappings).every((mappings) =>
      mappings.every((m) => m.systemValue !== '')
    );
    const allRcMapped = Object.values(rcMappings).every((mappings) =>
      mappings.every((m) => m.systemValue !== '')
    );
    const allMaterialTypeMapped = materialTypeMappings.every((m) => m.systemValue !== '');

    return (
      allCofMapped &&
      allDcMapped &&
      allEddMapped &&
      allGoverningMapped &&
      allRcMapped &&
      allMaterialTypeMapped
    );
  }, [cofMappings, dcMappings, eddMappings, governingMappings, rcMappings, materialTypeMappings]);

  const handleNext = () => {
    if (!editableEsSheet) return;

    const valueMappings: Record<string, Record<string, string>> = {};

    COF_COLUMNS.forEach((column) => {
      const columnMappings = cofMappings[column] || [];
      valueMappings[column] = columnMappings.reduce(
        (acc, mapping) => {
          acc[mapping.userValue] = mapping.systemValue;
          return acc;
        },
        {} as Record<string, string>
      );
    });

    DC_COLUMNS.forEach((column) => {
      valueMappings[column] = dcMappings.reduce(
        (acc, mapping) => {
          acc[mapping.userValue] = mapping.systemValue;
          return acc;
        },
        {} as Record<string, string>
      );
    });

    EDD_COLUMNS.forEach((column) => {
      valueMappings[column] = eddMappings.reduce(
        (acc, mapping) => {
          acc[mapping.userValue] = mapping.systemValue;
          return acc;
        },
        {} as Record<string, string>
      );
    });

    GOVERNING_COLUMNS.forEach((column) => {
      const columnMappings = governingMappings[column] || [];
      valueMappings[column] = columnMappings.reduce(
        (acc, mapping) => {
          acc[mapping.userValue] = mapping.systemValue;
          return acc;
        },
        {} as Record<string, string>
      );
    });

    RC_COLUMNS.forEach((column) => {
      const columnMappings = rcMappings[column] || [];
      valueMappings[column] = columnMappings.reduce(
        (acc, mapping) => {
          acc[mapping.userValue] = mapping.systemValue;
          return acc;
        },
        {} as Record<string, string>
      );
    });

    MATERIAL_TYPE_COLUMNS.forEach((column) => {
      valueMappings[column] = materialTypeMappings.reduce(
        (acc, mapping) => {
          acc[mapping.userValue] = mapping.systemValue;
          return acc;
        },
        {} as Record<string, string>
      );
    });

    const updatedSheet = {
      ...editableEsSheet,
      valueMappings,
    };
    setEditableEsSheet(updatedSheet);

    // Smart navigation: skip to next step that needs attention
    const nextStep = getNextValidStep(updatedSheet);
    goToStep(nextStep);
  };

  const unmappedCount = useMemo(() => {
    let count = 0;
    Object.values(cofMappings).forEach((mappings) => {
      count += mappings.filter((m) => m.systemValue === '').length;
    });
    count += dcMappings.filter((m) => m.systemValue === '').length;
    count += eddMappings.filter((m) => m.systemValue === '').length;
    Object.values(governingMappings).forEach((mappings) => {
      count += mappings.filter((m) => m.systemValue === '').length;
    });
    Object.values(rcMappings).forEach((mappings) => {
      count += mappings.filter((m) => m.systemValue === '').length;
    });
    count += materialTypeMappings.filter((m) => m.systemValue === '').length;
    return count;
  }, [cofMappings, dcMappings, eddMappings, governingMappings, rcMappings, materialTypeMappings]);

  if (!originalEsSheet || !editableEsSheet) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          No data available. Please upload a file first.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack width="100%" minWidth="85.4rem" paddingBottom="6.4rem" gap="1.6rem">
      <Stack gap="3.2rem">
        <PanelHeader>
          <Typography variant="h3" fontWeight={500} color="primary.600">
            Map Values for Constrained Fields
          </Typography>
          <Typography variant="h5" fontWeight={400} color="primary.400">
            Map the values from your file to the allowed values in ES Tool
          </Typography>
        </PanelHeader>
        <Grow in={unmappedCount > 0} timeout={300} style={{ transitionDelay: '100ms' }}>
          <div>
            <Banner
              message="Some values need to be mapped before you can continue."
              severity="warning"
            />
          </div>
        </Grow>
      </Stack>
      <Stack width="100%" justifyContent="center" direction="row">
        <Stack spacing="2.4rem" width="100%">
          {COF_COLUMNS.map((column) => (
            <Box key={column}>
              <MappingSection
                title={column}
                mappings={cofMappings[column] || []}
                validValues={COF_VALID_VALUES}
                onChange={(index, value) => handleCofMappingChange(column, index, value)}
              />
            </Box>
          ))}

          <MappingSection
            title="Damage Class"
            mappings={dcMappings}
            validValues={DC_VALID_VALUES}
            onChange={handleDcMappingChange}
          />

          <MappingSection
            title="EDD Method"
            mappings={eddMappings}
            validValues={EDD_VALID_VALUES}
            onChange={handleEddMappingChange}
          />

          {GOVERNING_COLUMNS.map((column) => (
            <Box key={column}>
              <MappingSection
                title={column}
                mappings={governingMappings[column] || []}
                validValues={GOVERNING_VALID_VALUES}
                onChange={(index, value) => handleGoverningMappingChange(column, index, value)}
              />
            </Box>
          ))}

          {RC_COLUMNS.map((column) => (
            <Box key={column}>
              <MappingSection
                title={column}
                mappings={rcMappings[column] || []}
                validValues={RC_VALID_VALUES}
                onChange={(index, value) => handleRcMappingChange(column, index, value)}
              />
            </Box>
          ))}

          <MappingSection
            title="Material Type"
            mappings={materialTypeMappings}
            validValues={MATERIAL_TYPE_VALID_VALUES}
            onChange={handleMaterialTypeMappingChange}
          />
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
        <Box />
        <Stack direction="row" gap={2}>
          <Button
            variant="outlined"
            onClick={goToPreviousStep}
            sx={{
              textTransform: 'none',
              borderColor: abyssColors.primary[300],
              color: abyssColors.primary[600],
              '&:hover': {
                borderColor: abyssColors.primary[400],
                backgroundColor: abyssColors.primary[50],
              },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!areAllValuesMapped}
            sx={{
              textTransform: 'none',
            }}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
